import {useEffect, useState} from "react";
import baseUrl from "../../../../baseUrl";
import {BsFillTrash2Fill, BsEraserFill, BsChatLeftText, BsChatLeftTextFill} from "react-icons/bs";
import {requester} from "../../../../services/requester";
import {useForm} from "../../../../hooks/useForm";
import {useConfirm} from "../../../../contexts/ConfirmContext";
import {hasNote} from "../../../../utils/notes";
import styles from "../DrawingsInfo.module.css";

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const DrawingInfo = ({
                                id,
                                mpo_number,
                                massif_number,
                                area,
                                crop,
                                note,
                                created_at,
                                verified,
                                verified_by,
                                verified_on,
                                onDrawingEditDelete,
                                cropsOptions,
                                settlementCompanies = []
                            }) => {
    const request = requester();
    const confirm = useConfirm();
    // Ш1/Ш2: двата подреда са взаимно изключващи се — отварянето на единия
    // затваря другия, за да не увиснат две форми под един и същ ред.
    const [open, setOpen] = useState(null);          // null | 'edit' | 'note'
    const [busyVerify, setBusyVerify] = useState(false);
    const [noteText, setNoteText] = useState(note || '');
    const [savingNote, setSavingNote] = useState(false);
    // Щ5: на кого да се прехвърли редът. Празно значи „остава тук".
    const [transferTo, setTransferTo] = useState('');

    const {values, changeHandler, onSubmitEditValues} = useForm({
        mpo_number: mpo_number,
        massif_number: massif_number,
        area: area,
        crop: crop
    });

    useEffect(() => {
        onSubmitEditValues({
            mpo_number: mpo_number,
            massif_number: massif_number,
            area: area,
            crop: crop
        })
    }, [area, massif_number, mpo_number, crop])

    // Ш2: подредът за забележка тръгва от записаното. Без това редакция, която
    // е отменена със „Затвори", би оставила стария текст в полето.
    useEffect(() => {
        setNoteText(note || '');
    }, [note]);

    // Ш1: „Затвори" не запазва нищо — връща полетата към записаното и
    // прибира подреда. Точно за случая „отворих по погрешка" или
    // „промених половината и се отказах".
    const затвори = () => {
        onSubmitEditValues({mpo_number, massif_number, area, crop});
        setNoteText(note || '');
        setTransferTo('');
        setOpen(null);
    };

    const превключи = (кой) => setOpen(текущ => {
        if (текущ === кой) {
            onSubmitEditValues({mpo_number, massif_number, area, crop});
            setNoteText(note || '');
            setTransferTo('');
            return null;
        }
        setTransferTo('');
        return кой;
    });

    const onClickDeleteHandle = async () => {
        // Р7: едно и също потвърждение навсякъде (contexts/ConfirmContext).
        // Досега тук се отваряше втори ред „Изтриване на този запис?" — работеше,
        // но беше различен навик от останалите екрани.
        if (!await confirm({
            title: "Да се изтрие ли този изчертан ред?",
            detail: `масив ${massif_number}, МПО ${mpo_number}, `
                + `${area?.toFixed(2)}`,
        })) return;

        request.delete(`${baseUrl()}/drawings/${id}`)
            .then(() => {
                onDrawingEditDelete();
            })
            .catch(err => alert(err.message || 'Грешка при изтриване.'));
    };

    // М2: отметка „Проверено". Праща се ИЗРИЧНАТА нова стойност, не „обърни я" —
    // при два бързи клика или стара отворена страница превключването на сървъра
    // би дало обратното на очакваното.
    const onClickVerifyHandle = () => {
        setBusyVerify(true);
        request.put(`${baseUrl()}/drawings/${id}/verified`, {verified: !verified})
            .then(() => onDrawingEditDelete())
            .catch(err => alert(err.message || 'Грешка при отмятането.'))
            .finally(() => setBusyVerify(false));
    };

    const verifiedTitle = verified
        ? `Проверено${verified_by ? ` от ${verified_by}` : ''}`
          + `${verified_on ? ` на ${formatDate(verified_on)}` : ''}`
          + ' — натиснете, за да го върнете за проверка'
        : 'Отбележете реда като сверен с външния източник';

    const onClickEditHandle = async (e) => {
        e.preventDefault();

        const body = {
            mpo_number: Number(values.mpo_number),
            massif_number: Number(values.massif_number),
            area: Number(values.area),
            crop: values.crop
        };
        // Щ5: булстатът тръгва САМО когато е избран друг ползвател — иначе
        // всяка обикновена редакция би пращала „прехвърли на себе си".
        if (transferTo) body.bullstat = Number(transferTo);

        // Прехвърлянето маха реда от този екран и го слага при другия —
        // затова се пита, както при изтриване.
        if (transferTo) {
            const име = settlementCompanies
                .find(c => String(c.bullstat) === String(transferTo))?.company_name;
            if (!await confirm({
                title: "Да се прехвърли ли редът на друг ползвател?",
                message: "Редът ще изчезне от този ползвател и ще се появи при избрания. "
                    + "Площта влиза в неговото изчертано.",
                detail: `масив ${values.massif_number}, ${Number(values.area).toFixed(2)} → ${име}`,
                confirmText: "Да, прехвърли",
            })) return;
        }

        try {
            await request.put(`${baseUrl()}/drawings/${id}`, body);
            onDrawingEditDelete();
            setTransferTo('');
            setOpen(null);
        } catch (error) {
            alert(error.message || 'Възникна грешка при заявката.');
        }
    };

    // Ш2: забележката минава през СВОЙ ендпойнт. Формата не показва масив, МПО
    // и площ — ако вървеше през общата редакция, щеше да ги връща към
    // стойностите, заварени при отварянето на страницата.
    const onClickSaveNote = async () => {
        setSavingNote(true);
        try {
            await request.put(`${baseUrl()}/drawings/${id}/note`, {note: noteText});
            onDrawingEditDelete();
            setOpen(null);
        } catch (error) {
            alert(error.message || 'Забележката не се записа.');
        } finally {
            setSavingNote(false);
        }
    };

    // Ш2: пълна забележка → плътна тъмна икона; празна → бледа контурна.
    const имаЗабележка = hasNote(note);
    const noteTitle = имаЗабележка
        ? `Забележка: ${note}`
        : 'Добавете забележка към този ред';

    return (
        <>
            {/* М2: сверен ред стои на блед фон — вижда се на един поглед докъде
                е стигнала проверката, без да се чете колона по колона. */}
            <tr style={verified ? {background: "var(--app-verified-bg)"} : undefined}
                title={verified ? verifiedTitle : undefined}>
                <td className={`text-center ${styles.colDate}`} style={{paddingTop: "14px"}}>{formatDate(created_at)}</td>
                <td className="text-center" style={{paddingTop: "14px"}}>{massif_number}</td>
                <td className="text-center" style={{paddingTop: "14px"}}>{mpo_number}</td>
                <td className="text-center" style={{paddingTop: "14px"}}>{area?.toFixed(2)}</td>
                <td className="text-center" style={{paddingTop: "14px"}}>{crop}</td>
                {/* Ш3: забележката се вижда и на самия ред, не само в справките. */}
                <td className={styles.colNote} style={{paddingTop: "14px"}}>{note}</td>
                <td>
                    <button onClick={() => превключи('edit')} data-bs-toggle="tooltip" title="Редакция"
                            type="button" className="btn btn-warning"><BsEraserFill/></button>
                </td>
                <td>
                    <button onClick={onClickDeleteHandle} data-bs-toggle="tooltip" title="Изтриване" type="button" className="btn btn-danger"><BsFillTrash2Fill/>
                    </button>
                </td>
                {/* Ш2: „Забележка" стои между изтриването и „Проверено" — така е поискана. */}
                <td>
                    <button onClick={() => превключи('note')} data-bs-toggle="tooltip" title={noteTitle}
                            type="button"
                            className={имаЗабележка ? "btn btn-dark" : "btn btn-outline-secondary"}>
                        {имаЗабележка ? <BsChatLeftTextFill/> : <BsChatLeftText/>}
                    </button>
                </td>
                <td>
                    <button onClick={onClickVerifyHandle} disabled={busyVerify}
                            title={verifiedTitle} type="button"
                            style={{whiteSpace: "nowrap"}}
                            className={verified ? "btn btn-outline-secondary" : "btn btn-success"}>
                        {verified ? "За проверка" : "Проверено"}
                    </button>
                </td>

            </tr>

            {open === 'edit' && (
                <tr style={{background: "var(--app-neutral-bg)"}}>
                    {/* Колоната „Дата" няма поле за редакция, но клетката е
                        задължителна — без нея целият ред се измества с една
                        колона наляво и полето за култура застава под „Площ",
                        а под „Култура" остава бутонът (затова изглеждаше, че
                        култура не може да се добавя). */}
                    <td></td>
                    <td className="text-center" style={{paddingTop: "12px"}}><input onChange={changeHandler} size={5} type="text"
                                                       value={values.massif_number} name="massif_number"/></td>
                    <td className="text-center" style={{paddingTop: "12px"}}><input onChange={changeHandler} size={5} type="text"
                                                       value={values.mpo_number} name="mpo_number"/></td>
                    <td className="text-center" style={{paddingTop: "12px"}}><input onChange={changeHandler} size={5} type="text" value={values.area}
                                                       name="area"/></td>
                    <td className="text-center" style={{paddingTop: "12px"}}>
                        <input onChange={changeHandler} size={5} type="search" list="crops-list" value={values.crop}
                                                       name="crop"/>
                        <datalist id="crops-list">
                            {cropsOptions.map(d => <option key={d.id} value={d.value}/>)}
                        </datalist>
                    </td>
                    {/* Забележката се пипа от своя бутон, не оттук — иначе
                        едната форма щеше да презаписва другата.
                        Щ5: тук стои прехвърлянето към друг ползвател в същото
                        землище. Празно = редът си остава при този ползвател. */}
                    <td className="text-center" style={{paddingTop: "12px"}}>
                        {settlementCompanies.length > 0 && (
                            <select className="form-select form-select-sm"
                                    value={transferTo}
                                    title="Прехвърляне на реда към друг ползвател в това землище"
                                    onChange={e => setTransferTo(e.target.value)}>
                                <option value="">— прехвърли на…</option>
                                {settlementCompanies.map(c => (
                                    <option key={c.bullstat} value={c.bullstat}>{c.company_name}</option>
                                ))}
                            </select>
                        )}
                    </td>
                    {/* colSpan 4 — колоните за действия са четири с „Забележка" */}
                    <td colSpan={4}>
                        <div className="d-flex gap-2">
                            <button onClick={onClickEditHandle} style={{flex: 1}} type="button"
                                    className="btn btn-warning">
                                {transferTo ? 'Прехвърли' : 'Запази'}
                            </button>
                            {/* Ш1: изход без запис — за отворено по погрешка или
                                за половин променени стойности, които не бива да
                                се връщат една по една. */}
                            <button onClick={затвори} type="button"
                                    className="btn btn-outline-secondary">Затвори
                            </button>
                        </div>
                    </td>
                </tr>
            )}

            {open === 'note' && (
                <tr style={{background: "var(--app-neutral-bg)"}}>
                    <td></td>
                    <td colSpan={5} style={{paddingTop: "12px"}}>
                        <input value={noteText} onChange={e => setNoteText(e.target.value)}
                               className="form-control form-control-sm" maxLength={2000}
                               placeholder={`Забележка за масив ${massif_number}, МПО ${mpo_number}`}/>
                    </td>
                    <td colSpan={4}>
                        <div className="d-flex gap-2">
                            <button onClick={onClickSaveNote} disabled={savingNote} style={{flex: 1}}
                                    type="button" className="btn btn-warning">Запази
                            </button>
                            <button onClick={затвори} type="button"
                                    className="btn btn-outline-secondary">Затвори
                            </button>
                        </div>
                    </td>
                </tr>
            )}

        </>
    );
};
